import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn, faStar } from '@fortawesome/free-solid-svg-icons';
import LeadDrawer from './LeadDrawer';
import LeadsInsights from './LeadsInsights';
import LeadsCoverageMap from './LeadsCoverageMap';
import LeadSelect from './LeadSelect';
import { LEAD_SOURCES, DEFAULT_LEAD_SOURCE, LEAD_COUNTRIES } from './leadOptions';
import { formatPhone, waHref } from './phoneFormat';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import './leads.css';

/**
 * Leads — the new-inquiry pipeline, off the spreadsheet.
 *
 * Backfilled from "Cleaning notes - New Inquiries" (577 rows, Jul 2025 on).
 * The three things the sheet could not do, and which this page is built
 * around: work the follow-up pile by date, tie a conversion to the client it
 * became, and count PEOPLE rather than rows when a number writes in twice.
 */

const today = () => DateTime.now().setZone('Asia/Dubai').toFormat('yyyy-MM-dd');


export default function Leads() {
    const userRole = (localStorage.getItem('userRole') || '').toLowerCase();
    // Same office roles the server allows (owner, 2026-10-18).
    const canDelete = ['manager', 'admin', 'supervisor'].includes(userRole);

    const [meta, setMeta] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openId, setOpenId] = useState(null);
    const [showInsights, setShowInsights] = useState(false);
    const [showCoverage, setShowCoverage] = useState(false);
    const [staff, setStaff] = useState([]);
    const [areas, setAreas] = useState([]);

    // The daily 16:30 notification links straight to the leads it counted, so
    // ?due=true / ?status=quoted have to actually arrive as filters.
    const params = new URLSearchParams(useLocation().search);
    /* TWO COPIES OF THE FILTERS, ON PURPOSE (2026-09-01). `draft` is what the bar
       shows and what typing changes; `applied` is what the table is actually
       built from. Nothing reloads until Apply moves one onto the other.

       Before this, every keystroke and every dropdown fired a request, so
       composing "quoted + this area + last month" cost three round trips and the
       rows kept moving under you while you were still choosing. */
    const [applied, setApplied] = useState(() => ({
        search: '', status: params.get('status') || '', lead_type: '', lost_reason: '',
        area: '', assigned_to: '', from: '', to: '',
        due: params.get('due') === 'true', starred: false,
    }));
    /* The 16:30 notification deep-links with ?due=true / ?status=quoted, so the
       APPLIED copy starts from the URL too — not only the draft, or that link
       would land on an unfiltered table with the filters merely suggested. */
    const [draft, setDraft] = useState(applied);

    // Adding is a single line, always on screen. The sheet's one real
    // advantage was that a new row cost four keystrokes; a modal would cost
    // more than that before you typed anything.
    // WhatsApp is the default because it is where almost every inquiry lands.
    // A default that is right most of the time is one fewer thing to touch on a
    // form whose whole point is that it costs nothing to fill in.
    const [quick, setQuick] = useState({
        inquiry_date: today(), name: '', phone: '', email: '', units_count: '',
        lead_type: 'holiday_home_company', status: 'new', source: DEFAULT_LEAD_SOURCE,
    });
    const [adding, setAdding] = useState(false);

    // Source decides what the contact field IS. Chosen third, before the
    // contact, so the field has already become the right one by the time
    // anybody types into it. Every channel but Email leaves us a NUMBER — a
    // cleaner passing on a unit hands over a phone number like anybody else —
    // so the phone input is the default and email is the exception.
    const contactMode = quick.source === 'Email' ? 'email' : 'phone';

    useEffect(() => {
        axios.get('/api/leads/meta').then((r) => setMeta(r.data)).catch(() => { });
        axios.get('/api/leads/staff').then((r) => setStaff(r.data.staff || [])).catch(() => { });
        axios.get('/api/leads/areas').then((r) => setAreas(r.data.areas || [])).catch(() => { });
    }, []);

    // Typing in the search box should not fire a request per keystroke.

    const query = useMemo(() => {
        const p = {};
        Object.entries(applied).forEach(([k, v]) => {
            if (v === '' || v === false || v == null) return;
            p[k] = v === true ? 'true' : v;
        });
        return p;
    }, [applied]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [list, s] = await Promise.all([
                axios.get('/api/leads', { params: query }),
                axios.get('/api/leads/stats', { params: { from: applied.from || undefined, to: applied.to || undefined } }),
            ]);
            setRows(list.data.leads || []);
            setTotal(list.data.total || 0);
            setStats(s.data);
            setError('');
        } catch (e) {
            setError(e.response?.data?.error || 'Could not load the leads.');
        } finally { setLoading(false); }
    }, [query, applied.from, applied.to]);

    useEffect(() => { load(); }, [load]);

    const label = (group, key) => meta?.labels?.[group]?.[key] || key;
    // A plain YYYY-MM-DD is a CALENDAR DATE, not an instant: it is rendered as
    // typed. Anything carrying a time is a real timestamp and is read in Dubai.
    // Running a bare date through a zone is how a row lands a day early.
    const d = (v, fmt = 'd LLL yy') => {
        if (!v) return '';
        const s = String(v);
        const dt = /^\d{4}-\d{2}-\d{2}$/.test(s)
            ? DateTime.fromISO(s)
            : DateTime.fromISO(s, { zone: 'utc' }).setZone('Asia/Dubai');
        return dt.toFormat(fmt);
    };

    // A phone input that has only been touched leaves the dial code behind
    // ("+971"), which is not a number anybody can call.
    const quickPhone = quick.phone.replace(/[^0-9]/g, '').length >= 8 ? quick.phone : '';
    const canAdd = !!(quick.name.trim() || quickPhone || quick.email.trim());

    const addLead = async () => {
        if (!canAdd) return;
        setAdding(true);
        try {
            const body = { ...quick, phone: quickPhone };
            await axios.post('/api/leads', body);
            setQuick((q) => ({ ...q, name: '', phone: '', email: '', units_count: '' }));
            load();
        } catch (e) {
            setError(e.response?.data?.error || 'Could not save that lead.');
        } finally { setAdding(false); }
    };


    const t = stats?.totals || {};
    const setF = (k, v) => setDraft((f) => ({ ...f, [k]: v }));

    /* Same shape both sides, so a plain stringify is a fair comparison and the
       button can stay dead until there is genuinely something to apply. */
    const dirty = JSON.stringify(draft) !== JSON.stringify(applied);
    const applyFilters = () => setApplied(draft);
    const EMPTY_FILTERS = {
        search: '', status: '', lead_type: '', lost_reason: '', area: '',
        assigned_to: '', from: '', to: '', due: false, starred: false,
    };
    const clearFilters = () => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); };
    // Enter is how people submit a search box; not honouring it reads as broken.
    const onFilterKey = (e) => { if (e.key === 'Enter') applyFilters(); };

    // A follow-up date that has arrived is the only thing on this page that is
    // asking for something today, so it is the only thing coloured.
    const followUpCell = (row) => {
        if (!row.next_follow_up_on) return <span className="lds__muted">—</span>;
        const day = String(row.next_follow_up_on).slice(0, 10);
        const closed = ['registered', 'booked', 'lost'].includes(row.status);
        const due = !closed && day <= today();
        return <span className={due ? 'lds__due' : 'lds__soon'}>{d(row.next_follow_up_on)}</span>;
    };

    return (
        <div className="lds">
            <div className="lds__head">
                <div>
                    <h1 className="lds__title">
                        <FontAwesomeIcon icon={faBullhorn} /> Leads
                    </h1>
                    <p className="lds__sub">
                        Every new inquiry, who it came from and what happened to it.
                    </p>
                </div>
                <div className="lds__headActions">
                    <button className={`lds__btn${showCoverage ? ' lds__btn--on' : ''}`}
                        onClick={() => setShowCoverage((v) => !v)}>
                        {showCoverage ? 'Hide coverage' : 'Coverage area'}
                    </button>
                    <button className={`lds__btn${showInsights ? ' lds__btn--on' : ''}`}
                        onClick={() => setShowInsights((v) => !v)}>
                        {showInsights ? 'Hide statistics' : 'Statistics'}
                    </button>
                </div>
            </div>

            {error && <div className="lds__err">{error}</div>}

            <div className="lds__stats">
                <div className="lds__stat">
                    <div className="lds__statLabel">Inquiries</div>
                    <div className="lds__statValue">{t.inquiries ?? '—'}</div>
                    <div className="lds__statNote">{t.people ?? 0} different people</div>
                </div>
                {/* Two ends, kept apart: opening an account is not the same as
                    giving us work, and one number hid the difference. */}
                <div className="lds__stat">
                    <div className="lds__statLabel">Registered</div>
                    <div className="lds__statValue">{t.converted_people ?? '—'}</div>
                    <div className="lds__statNote">
                        {t.conversion_rate == null ? '—' : `${t.conversion_rate.toFixed(1)}% of people`}
                    </div>
                </div>
                <div className="lds__stat">
                    <div className="lds__statLabel">Made a booking</div>
                    <div className="lds__statValue">{t.booked_people ?? '—'}</div>
                    <div className="lds__statNote">
                        {t.registered ? `${t.registered} signed up - no bookings` : 'of those who registered'}
                    </div>
                </div>
                <div className="lds__stat">
                    <div className="lds__statLabel">Still open</div>
                    <div className="lds__statValue">{t.open ?? '—'}</div>
                    <div className="lds__statNote">not yet won or lost</div>
                </div>
                <div className={`lds__stat${t.due ? ' lds__stat--alert' : ''}`}>
                    <div className="lds__statLabel">Follow up today</div>
                    <div className="lds__statValue">{t.due ?? '—'}</div>
                    <div className="lds__statNote">due or overdue</div>
                </div>
                {/* Only once there is something real to average. The figure
                    counts leads LOGGED WHEN THEY CAME IN and later converted;
                    a row imported from the sheet months after its inquiry
                    would otherwise report the age of the backlog as a decision
                    time (it read 302 days on two such rows, 2026-08-30). The
                    sample size is on the card so the number is never a
                    mystery again. */}
                {t.avg_days_to_convert != null && t.converted_measured > 0 && (
                    <div className="lds__stat">
                        <div className="lds__statLabel">Time to convert</div>
                        <div className="lds__statValue">{Math.round(t.avg_days_to_convert)}</div>
                        <div className="lds__statNote">
                            days, on average · {t.converted_measured} lead{t.converted_measured === 1 ? '' : 's'}
                        </div>
                    </div>
                )}
            </div>

            {showCoverage && <LeadsCoverageMap onClose={() => setShowCoverage(false)} />}

            {showInsights && <LeadsInsights stats={stats} labels={meta?.labels} />}


            <div className="lds__quick">
                <input type="date" value={quick.inquiry_date}
                    onChange={(e) => setQuick((q) => ({ ...q, inquiry_date: e.target.value }))} />
                <input className="lds__quickName" placeholder="Customer name" value={quick.name}
                    onChange={(e) => setQuick((q) => ({ ...q, name: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && addLead()} />
                {/* Not clearable: a lead always came from somewhere, and an empty
                    source is only ever an accident. */}
                <LeadSelect
                    className="lds__quickSource"
                    options={LEAD_SOURCES.map((x) => ({ value: x, label: x }))}
                    value={quick.source}
                    onChange={(v) => setQuick((q) => ({ ...q, source: v || DEFAULT_LEAD_SOURCE }))}
                />
                {contactMode === 'phone' && (
                    <PhoneInput
                        defaultCountry="ae"
                        countries={LEAD_COUNTRIES}
                        value={quick.phone}
                        onChange={(v) => setQuick((q) => ({ ...q, phone: v }))}
                        placeholder="Number"
                        className="client-phone-wrapper lds__phoneWrap"
                        inputClassName="client-phone-input"
                    />
                )}
                {contactMode === 'email' && (
                    <input className="lds__quickPhone" type="email" placeholder="Email"
                        value={quick.email}
                        onChange={(e) => setQuick((q) => ({ ...q, email: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && addLead()} />
                )}
                {/* How many units, not what kind of client. On a first contact
                    the size of the portfolio is the thing worth writing down —
                    the type can be set later in the drawer, and is the same
                    answer for nine leads in ten anyway. */}
                <input className="lds__quickUnits" type="number" min="0" placeholder="Units"
                    value={quick.units_count}
                    onChange={(e) => setQuick((q) => ({ ...q, units_count: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && addLead()} />
                <button className="lds__btn lds__btn--primary" onClick={addLead}
                    disabled={adding || !canAdd}>
                    {adding ? 'Adding…' : 'Add lead'}
                </button>
                <p className="lds__quickHint">
                    Name or number is enough — everything else can be filled in later by
                    opening the row.
                </p>
            </div>

            <div className="lds__filters">
                <input className="lds__search" placeholder="Search name, number, area or note…"
                    value={draft.search} onChange={(e) => setF('search', e.target.value)}
                    onKeyDown={onFilterKey} />
                <LeadSelect
                    className="lds__select"
                    options={(meta?.statuses || []).map((x) => ({ value: x, label: label('status', x) }))}
                    value={draft.status}
                    onChange={(v) => setF('status', v)}
                    placeholder="All statuses"
                    isClearable
                />
                <LeadSelect
                    className="lds__select"
                    options={(meta?.lead_types || []).map((x) => ({ value: x, label: label('lead_type', x) }))}
                    value={draft.lead_type}
                    onChange={(v) => setF('lead_type', v)}
                    placeholder="All types"
                    isClearable
                />
                <LeadSelect
                    className="lds__select"
                    options={areas.map((a) => ({ value: a, label: a }))}
                    value={draft.area}
                    onChange={(v) => setF('area', v)}
                    placeholder="Any area"
                    isClearable
                />
                <LeadSelect
                    className="lds__select"
                    options={(meta?.lost_reasons || []).map((x) => ({ value: x, label: label('lost_reason', x) }))}
                    value={draft.lost_reason}
                    onChange={(v) => setF('lost_reason', v)}
                    placeholder="Any reason lost"
                    isClearable
                />
                <span className="lds__range">
                    <input className="lds__date" type="date" value={draft.from}
                        onChange={(e) => setF('from', e.target.value)} onKeyDown={onFilterKey} />
                    <span className="lds__filterSep">to</span>
                    <input className="lds__date" type="date" value={draft.to}
                        onChange={(e) => setF('to', e.target.value)} onKeyDown={onFilterKey} />
                </span>
                <button className={`lds__btn${draft.due ? ' lds__btn--on' : ''}`}
                    onClick={() => setF('due', !draft.due)}>Due now</button>
                <button className={`lds__btn${draft.starred ? ' lds__btn--on' : ''}`}
                    onClick={() => setF('starred', !draft.starred)}>Starred</button>
                <LeadSelect
                    className="lds__select"
                    options={[{ value: 'none', label: 'Nobody yet' }].concat(
                        staff.map((u) => ({ value: u.id, label: [u.first_name, u.last_name].filter(Boolean).join(' ') }))
                    )}
                    value={draft.assigned_to}
                    onChange={(v) => setF('assigned_to', v)}
                    placeholder="Anyone"
                    isClearable
                />
                {/* Apply carries the whole bar at once. It goes dead the moment
                    the draft matches what is already on screen, so the button
                    itself tells you whether there is anything outstanding.

                    Kept in one box with Clear: the bar wraps, and left loose the
                    two split across lines with Clear stranded on a row of its
                    own. They are the bar's actions and they belong together. */}
                <span className="lds__filterActions">
                    {/* Clear first, then Apply — the destructive one never sits
                        where a hand travelling to the primary button lands. */}
                    <button className="lds__btn" onClick={clearFilters}>
                        Clear
                    </button>
                    <button className={`lds__btn lds__btn--apply${dirty ? ' is-dirty' : ''}`}
                        onClick={applyFilters} disabled={!dirty}
                        title={dirty ? 'Apply these filters' : 'Filters are up to date'}>
                        Apply{dirty ? ' •' : ''}
                    </button>
                </span>
            </div>

            <div className="lds__tableWrap">
                <table className="lds__table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Name</th>
                            <th>Contact</th>
                            <th>Units</th>
                            <th>Status</th>
                            <th>Area / why lost</th>
                            <th>Follow up</th>
                            <th>Handled by</th>
                            <th>Client</th>
                            <th aria-label="Starred" />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} onClick={() => setOpenId(r.id)}>
                                <td className="lds__cellDate">{d(r.inquiry_date)}</td>
                                <td className="lds__cellName">
                                    {r.name || <span className="lds__anon">No name</span>}
                                    {r.other_inquiries > 0 && (
                                        <span className="lds__repeat" title="This number has been in touch before">
                                            +{r.other_inquiries}
                                        </span>
                                    )}
                                </td>
                                <td className="lds__phone">
                                    {/* One tap to the chat. stopPropagation so the link
                                        opens WhatsApp instead of the row's drawer. */}
                                    {r.phone && waHref(r.phone) ? (
                                        <a className="lds__link" href={waHref(r.phone)} target="_blank"
                                            rel="noreferrer" title="Open this chat in WhatsApp"
                                            onClick={(e) => e.stopPropagation()}>
                                            {formatPhone(r.phone)}
                                        </a>
                                    ) : r.phone ? formatPhone(r.phone)
                                        : r.email ? (
                                            <a className="lds__link" href={`mailto:${r.email}`}
                                                onClick={(e) => e.stopPropagation()}>{r.email}</a>
                                        ) : <span className="lds__muted">—</span>}
                                </td>
                                <td className="lds__cellUnits" title={label('lead_type', r.lead_type)}>
                                    {r.units_count != null ? r.units_count : <span className="lds__muted">—</span>}
                                </td>
                                <td>
                                    <span className={`lds__chip lds__chip--${r.status}`}>{label('status', r.status)}</span>
                                </td>
                                <td className="lds__muted">
                                    {r.area || ''}
                                    {r.area && r.lost_reason ? ' · ' : ''}
                                    {r.lost_reason ? label('lost_reason', r.lost_reason) : ''}
                                    {!r.area && !r.lost_reason ? '—' : ''}
                                </td>
                                <td>
                                    {followUpCell(r)}
                                    {r.followed_up_by_name && (
                                        <div className="lds__subCell">
                                            last: {r.followed_up_by_name}
                                            {r.followed_up_at ? ` · ${d(r.followed_up_at)}` : ''}
                                        </div>
                                    )}
                                </td>
                                <td className="lds__muted">
                                    {r.assigned_to_name || <span className="lds__anon">Nobody</span>}
                                </td>
                                <td>
                                    {r.client_id ? (
                                        <a className="lds__link" href={`/clients/${r.client_id}`}
                                            onClick={(e) => e.stopPropagation()}>{r.client_name}</a>
                                    ) : <span className="lds__muted">—</span>}
                                </td>
                                <td>
                                    {/* Only the ones that are starred. A greyed-out star on
                                        every other row is 567 pieces of furniture. */}
                                    {r.starred && <FontAwesomeIcon icon={faStar} className="lds__star" />}
                                </td>
                            </tr>
                        ))}
                        {!rows.length && !loading && (
                            <tr><td colSpan={10} className="lds__empty">No leads match those filters.</td></tr>
                        )}
                        {loading && !rows.length && (
                            <tr><td colSpan={10} className="lds__empty">Loading…</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            <p className="lds__count">
                Showing {rows.length} of {total}
                {rows.length < total ? ' — narrow the filters to see the rest' : ''}
            </p>

            {openId && (
                <LeadDrawer
                    leadId={openId}
                    meta={meta}
                    staff={staff}
                    areas={areas}
                    canDelete={canDelete}
                    onClose={() => setOpenId(null)}
                    onSaved={load}
                />
            )}
        </div>
    );
}
